<script lang="ts" setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n';
import { useCountriesStore } from '@/stores/countries';
import { storeToRefs } from 'pinia';

const emits = defineEmits(['selected']);

const { locale } = useI18n();

const addresse = defineModel({
    default: ''
})
const props = defineProps({
    readonly: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: 'Entrez votre adresse…'
    }
})

interface Suggestion {
    title: string;
    id: string;
}

const countriesStore = useCountriesStore();
const { countries } = storeToRefs(countriesStore);
const suggestions = ref<Suggestion[]>([]);
const noResult = ref(false);
const loading = ref(false);
const showResults = ref(false);
const selectedIndex = ref(-1);
let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
const shouldFetch = ref(true);

const countryList = computed(() => countries.value.map(c => c.iso3).join(','));

// Replace with your actual HERE API key
const HERE_API_KEY = 'QHOOIUghqHgQPuohnZg94fWMzH0ZDiTcABIIIdjWx90';

async function fetchSuggestions(query: string) {
    loading.value = true;
    try {
        const params = new URLSearchParams({
            q: query,
            apiKey: HERE_API_KEY,
            lang: locale.value,
            in: 'countryCode:'+countryList.value

        });
        suggestions.value = [];
        showResults.value = true;
        const response = await fetch(`https://autocomplete.search.hereapi.com/v1/autocomplete?${params}`);
        const data = await response.json();
        suggestions.value = data.items || [];
        noResult.value = suggestions.value.length === 0;
    } catch (error) {
        console.error('Error fetching suggestions:', error);
        suggestions.value = [];
        noResult.value = true;
    } finally {
        loading.value = false;
        selectedIndex.value = -1;
    }
}

watch(addresse, (newVal) => {
    if (debounceTimeout) {
        clearTimeout(debounceTimeout);
    }
    // Only search if input has 3 or more words
    const words = newVal.trim().split(/\s+/).filter(word => word.length > 0);
    if (words.length >= 3) {
        if (shouldFetch.value) {
            debounceTimeout = setTimeout(() => {
                fetchSuggestions(newVal);
            }, 300);
        }
        shouldFetch.value = true;
    } else {
        suggestions.value = [];
        noResult.value = false;
    }
});

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (suggestions.value.length > 0) {
            selectedIndex.value = (selectedIndex.value + 1) % suggestions.value.length;
        }
    } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (suggestions.value.length > 0) {
            selectedIndex.value =
                (selectedIndex.value - 1 + suggestions.value.length) % suggestions.value.length;
        }
    } else if (e.key === 'Enter') {
        e.preventDefault();
        if (selectedIndex.value >= 0 && suggestions.value[selectedIndex.value]) {
            selectSuggestion(suggestions.value[selectedIndex.value]);
            e.preventDefault();
        }
    }
}

function selectSuggestion(suggestion) {
    shouldFetch.value = false;
    addresse.value = suggestion.address.label;
    suggestions.value = [];
    selectedIndex.value = -1;
    emits('selected', suggestion);
}
</script>
<template>
  <div class="relative">
    <input v-model="addresse" type="text" :readonly="props.readonly" class="form-input" :placeholder="props.placeholder" @keydown="onKeydown" />
    <div v-if="showResults" class="absolute z-10 w-full bg-white border border-gray-300 rounded-b shadow-lg">
        <div v-if="loading" class="pa-2">Chargement…</div>
        <div v-if="noResult" class="pa-2">Aucun résultat</div>
        <ul v-else>
            <li v-for="(suggestion, index) in suggestions" :key="suggestion.id" @click="selectSuggestion(suggestion)"  :class="{ 'bg-select': index === selectedIndex }" class="p-2 cursor-pointer"><AddressResult :result="suggestion" /></li>
        </ul>
    </div>
  </div>
</template>
<style lang="scss" scoped>
input{
    width: 100%;
    padding-left: 10px;
    padding-right: 10px;
    border-radius: 19px;
    border: none;
    background-color: #FFF;
    font-size: 14px;
    line-height: 32px;
    box-shadow: 0px 0px 4px 0px #00000040 inset;
    &[readonly]{
        background-color: #F0F0F0;
    }
}
ul{
    list-style: none;
    padding: 0;
    margin: 0;
}
li{
    list-style: none;
    border-top: 1px solid #EEE;
    &:first-child{
        border-top: none;
    }
    padding: 2px 5px;
    &.bg-select{
        background-color: #a8a7a7;
    }
    &:hover{
        background-color: #afaeae;
    }
}
</style>